import type { SearchDocumentResult, SearchDocumentsInput } from '@erp/rpc';
import { useDocumentSearch } from '@/api/rpc/hooks/accounts-payable/useDocumentSearch';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { useMemo, useState } from 'react';
import { useGT } from 'gt-react';
import {
  MagnifyingGlassIcon,
  FileTextIcon,
  ShoppingCartIcon,
  CurrencyDollarIcon,
  TruckIcon,
} from '@phosphor-icons/react';

// Status labels and variants for POs
const PO_STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PARTIALLY_RECEIVED: 'Partially Received',
  FULLY_RECEIVED: 'Fully Received',
  PARTIALLY_INVOICED: 'Partially Invoiced',
  FULLY_INVOICED: 'Fully Invoiced',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

const PO_STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  DRAFT: 'outline',
  PENDING_APPROVAL: 'secondary',
  APPROVED: 'default',
  SENT: 'default',
  PARTIALLY_RECEIVED: 'secondary',
  FULLY_RECEIVED: 'default',
  PARTIALLY_INVOICED: 'secondary',
  FULLY_INVOICED: 'outline',
  CLOSED: 'outline',
  CANCELLED: 'destructive',
};

// Status labels and variants for SOs
const SO_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  fulfilled: 'Fulfilled',
  closed: 'Closed',
};

const SO_STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  pending: 'secondary',
  in_progress: 'default',
  fulfilled: 'default',
  closed: 'outline',
};

// Status labels and variants for Shipments
const SHIPMENT_STATUS_LABELS: Record<string, string> = {
  need_to_be_scheduled: 'Needs Scheduling',
  scheduled: 'Scheduled',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const SHIPMENT_STATUS_VARIANTS: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  need_to_be_scheduled: 'secondary',
  scheduled: 'default',
  in_transit: 'default',
  delivered: 'default',
  cancelled: 'destructive',
};

const formatCurrencyValue = (
  value: string | undefined | null,
  currency = 'USD',
): string => {
  if (!value) return 'N/A';

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return value;

  return numeric.toLocaleString(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
};

function DocumentResultItem({
  document,
  currency,
}: {
  document: SearchDocumentResult;
  currency: string;
}) {
  const gt = useGT();
  const isPO = document.documentType === 'PURCHASE_ORDER';
  const isSO = document.documentType === 'SALES_ORDER';
  const isShipment = document.documentType === 'SHIPMENT';

  // Get status based on document type
  const status = isPO
    ? document.poStatus
    : isSO
      ? document.soStatus
      : document.shipmentStatus;

  const statusLabel = isPO
    ? (PO_STATUS_LABELS[status ?? ''] ?? status)
    : isSO
      ? (SO_STATUS_LABELS[status ?? ''] ?? status)
      : (SHIPMENT_STATUS_LABELS[status ?? ''] ?? status);

  const statusVariant = isPO
    ? (PO_STATUS_VARIANTS[status ?? ''] ?? 'outline')
    : isSO
      ? (SO_STATUS_VARIANTS[status ?? ''] ?? 'outline')
      : (SHIPMENT_STATUS_VARIANTS[status ?? ''] ?? 'outline');

  const partyName = isPO ? document.supplierName : document.customerName;

  const originId = isPO
    ? document.poOriginId
    : isSO
      ? document.soOriginId
      : document.originLoadId;

  const originSystem = isPO
    ? document.poOriginSystem
    : isSO
      ? document.soOriginSystem
      : document.shipmentOriginSystem;

  const originParts = isShipment
    ? [originSystem].filter(Boolean)
    : [
        originSystem,
        originId && originId !== document.referenceNumber ? originId : null,
      ].filter(Boolean);

  const originDisplay =
    originParts.length > 0 ? `Origin: ${originParts.join(' • ')}` : null;

  // For shipments, build a route display
  const shipmentRoute =
    isShipment && (document.origin || document.destination)
      ? `${document.origin ?? '?'} → ${document.destination ?? '?'}`
      : null;

  return (
    <div className="flex w-full items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isPO ? (
            <FileTextIcon className="h-3.5 w-3.5 text-blue-600 shrink-0" />
          ) : isSO ? (
            <ShoppingCartIcon className="h-3.5 w-3.5 text-green-600 shrink-0" />
          ) : (
            <TruckIcon className="h-3.5 w-3.5 text-orange-600 shrink-0" />
          )}
          <div className="truncate text-sm font-medium">
            {document.referenceNumber ||
              gt('No Reference', {
                context:
                  'AP document search: when a document has no reference number',
              })}
          </div>
          <Badge variant={statusVariant} className="text-[10px] shrink-0">
            {statusLabel}
          </Badge>
          {(isSO || isShipment) &&
            document.chargesCount &&
            document.chargesCount > 0 && (
              <Badge variant="outline" className="text-[10px] shrink-0 gap-1">
                <CurrencyDollarIcon className="h-2.5 w-2.5" />
                {document.chargesCount}
              </Badge>
            )}
        </div>

        <div className="mt-0.5 truncate text-xs text-muted-foreground">
          {isShipment
            ? [
                originDisplay,
                shipmentRoute ??
                  gt('Unknown Route', {
                    context:
                      'AP document search: shipment route could not be determined',
                  }),
                formatDateOnlyString(document.orderDate),
              ]
                .filter(Boolean)
                .join(' | ')
            : [
                partyName ??
                  gt('Unknown', {
                    context:
                      'AP document search: supplier or customer name missing',
                  }),
                formatDateOnlyString(document.orderDate),
                originDisplay,
              ]
                .filter(Boolean)
                .join(' | ')}
        </div>

        {/* Show matched line items if available */}
        {document.matchedLineItems && document.matchedLineItems.length > 0 && (
          <div className="mt-1 text-xs text-muted-foreground">
            {document.matchedLineItems.slice(0, 2).map((li) => {
              const prefix =
                li.lineNumber === null || li.lineNumber === undefined
                  ? ''
                  : `#${li.lineNumber} `;
              return (
                <div key={li.id} className="truncate">
                  {prefix}
                  {li.description ||
                    gt('No description', {
                      context:
                        'AP document search: matched line item has no description',
                    })}
                </div>
              );
            })}
          </div>
        )}

        {/* Show charges summary for SOs */}
        {isSO &&
          document.chargesSummary &&
          document.chargesSummary.length > 0 && (
            <div className="mt-1 text-xs text-muted-foreground flex gap-2">
              {document.chargesSummary.slice(0, 2).map((charge) => (
                <span key={charge.id} className="flex items-center gap-0.5">
                  <CurrencyDollarIcon className="h-2.5 w-2.5" />
                  {formatCurrencyValue(charge.amount, currency)}
                </span>
              ))}
            </div>
          )}
      </div>

      <div className="flex flex-col items-end gap-0.5 shrink-0">
        <div className="whitespace-nowrap text-xs font-medium">
          {isShipment
            ? ''
            : formatCurrencyValue(document.totalAmount, currency)}
        </div>
        <div className="whitespace-nowrap text-[10px] text-muted-foreground">
          {isPO
            ? gt('PO', {
                context:
                  'abbreviation for purchase order in document search result',
              })
            : isSO
              ? gt('SO', {
                  context:
                    'abbreviation for sales order in document search result',
                })
              : gt('Shipment', {
                  context: 'document type label for shipment in search result',
                })}
        </div>
      </div>
    </div>
  );
}

export interface DocumentAutocompleteProps {
  /** Invoice ID for context-aware ranking */
  invoiceId?: string;
  /** Supplier ID to filter POs (switches to PO-only mode) */
  supplierId?: string;
  /** Customer ID to filter SOs (switches to SO-only mode) */
  customerId?: string;
  /** Invoice date for relevance ranking */
  invoiceDate?: string;
  /** Invoice amount for relevance ranking */
  invoiceAmount?: string;
  /** Currency for display */
  currency?: string;
  /** Document IDs to exclude from results */
  excludeDocumentIds?: string[];
  /** Callback when a document is selected */
  onSelect: (document: SearchDocumentResult) => void;
  /** Callback to open advanced search modal */
  onAdvancedSearch?: () => void;
  /** Additional className */
  className?: string;
  /** Placeholder text */
  placeholder?: string;
}

export function DocumentAutocomplete({
  invoiceId,
  supplierId,
  customerId,
  invoiceDate,
  invoiceAmount,
  currency = 'USD',
  excludeDocumentIds,
  onSelect,
  onAdvancedSearch,
  className,
  placeholder,
}: DocumentAutocompleteProps) {
  const gt = useGT();
  const [search, setSearch] = useState('');
  // The query sent to the server, 250ms after typing settles (`onSearch`).
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [open, setOpen] = useState(false);

  const excludeSet = useMemo(
    () => new Set(excludeDocumentIds ?? []),
    [excludeDocumentIds],
  );

  // Determine mode based on supplier/customer
  // Note: We keep mode as ALL even with supplierId since shipments are also relevant for AP matching
  const mode = useMemo(() => {
    if (customerId && !supplierId) return 'SALES_ORDERS_ONLY' as const;
    return 'ALL' as const;
  }, [supplierId, customerId]);

  const searchInput: SearchDocumentsInput = {
    query: debouncedSearch.length > 0 ? debouncedSearch : undefined,
    mode,
    invoiceId,
    supplierId,
    customerId,
    invoiceDate,
    invoiceAmount,
    limit: 25,
    offset: 0,
  };

  const query = useDocumentSearch(searchInput, open);

  const resolvedPlaceholder =
    placeholder ??
    gt('Search POs, Sales Orders, or Shipments...', {
      context: 'placeholder for AP document search combobox',
    });

  const options = useMemo(() => {
    return query.results.filter((doc) => !excludeSet.has(doc.id));
  }, [query.results, excludeSet]);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* An action picker: a pick is handed over, never held as a value. */}
      <Combobox
        items={options}
        value={null}
        onValueChange={(_id, selected) => {
          if (!selected) {
            return;
          }

          onSelect(selected);
          setSearch('');
        }}
        getValue={(doc) => doc.id}
        getLabel={(doc) => doc.referenceNumber}
        indicator="none"
        renderItem={(doc) => (
          <DocumentResultItem document={doc} currency={currency} />
        )}
        search={search}
        onSearchChange={setSearch}
        // Server is responsible for search + ranking, so no client filter.
        onSearch={setDebouncedSearch}
        debounceMs={250}
        pinSelected={false}
        resetSearchOnClose={false}
        rememberItems={false}
        open={open}
        onOpenChange={setOpen}
        className="flex-1"
        contentClassName="w-full"
        labels={pickerLabels({
          placeholder: resolvedPlaceholder,
          search: gt('Search by reference number, description...', {
            context: 'placeholder inside AP document search dropdown',
          }),
          empty: query.isLoading
            ? gt('Searching...', {
                context: 'loading message while searching documents',
              })
            : gt('No matching documents found.', {
                context: 'empty state when document search returns no results',
              }),
        })}
      />

      {onAdvancedSearch && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          onClick={onAdvancedSearch}
          title={gt('Advanced Search', {
            context: 'tooltip on button to open advanced document search',
          })}
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
